export interface PixKeyInfo {
  type: 'CPF' | 'CNPJ' | 'Telefone' | 'Email' | 'Aleatoria' | 'Invalida' | 'Vazia';
  masked: string;
  isValid: boolean;
  raw: string;
  normalized: string;
  message?: string;
}

const onlyDigits = (value: string) => (value || '').replace(/\D+/g, '');

const normalizeEmail = (value: string) => (value || '').trim().toLowerCase();

const normalizePhoneForPix = (value: string) => {
  const raw = (value || '').trim();
  if (!raw) return '';

  const digits = onlyDigits(raw);

  // Accept local BR numbers and convert to PIX standard with country code.
  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`;
  }
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+${digits}`;
  }
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+${digits}`;
  }
  return raw;
};

const isValidCpf = (cpf: string) => {
  const normalized = onlyDigits(cpf);
  if (!/^\d{11}$/.test(normalized)) return false;
  if (/^(\d)\1{10}$/.test(normalized)) return false;

  const calcDigit = (base: string, factor: number) => {
    let total = 0;
    for (let i = 0; i < base.length; i += 1) {
      total += Number(base[i]) * factor;
      factor -= 1;
    }
    const mod = (total * 10) % 11;
    return mod === 10 ? 0 : mod;
  };

  const d1 = calcDigit(normalized.slice(0, 9), 10);
  const d2 = calcDigit(normalized.slice(0, 10), 11);
  return d1 === Number(normalized[9]) && d2 === Number(normalized[10]);
};

const isValidCnpj = (cnpj: string) => {
  const normalized = onlyDigits(cnpj);
  if (!/^\d{14}$/.test(normalized)) return false;
  if (/^(\d)\1{13}$/.test(normalized)) return false;

  const calcDigit = (base: string, weights: number[]) => {
    const sum = base.split('').reduce((acc, digit, idx) => acc + Number(digit) * weights[idx], 0);
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const d1 = calcDigit(normalized.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = calcDigit(normalized.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === Number(normalized[12]) && d2 === Number(normalized[13]);
};

const maskPhone = (value: string) => {
  const digits = onlyDigits(value);
  const local = digits.startsWith('55') ? digits.slice(2) : digits;
  const ddd = local.slice(0, 2);
  const suffix = local.slice(-4);
  return `(+55) ${ddd} *****-${suffix}`;
};

export const getPixKeyInfo = (chave: string): PixKeyInfo => {
  const raw = (chave || '').trim();

  if (!raw) {
    return {
      type: 'Vazia',
      masked: '',
      isValid: false,
      raw: '',
      normalized: '',
    };
  }

  const normalizedPhone = normalizePhoneForPix(raw);
  const email = normalizeEmail(raw);
  const digits = onlyDigits(raw);
  const cpfValid = /^\d{11}$/.test(digits) && isValidCpf(digits);

  if (/^[\w.+-]+@[\w.-]+\.[a-z]{2,}$/i.test(email)) {
    const [usuario, dominio] = email.split('@');
    return {
      type: 'Email',
      masked: `${usuario.slice(0, 1)}***@${dominio}`,
      isValid: true,
      raw,
      normalized: email,
    };
  }

  if (/^\d{11}$/.test(digits) && cpfValid) {
    return {
      type: 'CPF',
      masked: `***.${digits.slice(3, 6)}.${digits.slice(6, 9)}-**`,
      isValid: true,
      raw,
      normalized: digits,
    };
  }

  if (/^\+55\d{10,11}$/.test(normalizedPhone)) {
    return {
      type: 'Telefone',
      masked: maskPhone(normalizedPhone),
      isValid: true,
      raw,
      normalized: normalizedPhone,
      message: 'Telefone ajustado para o formato PIX com +55.',
    };
  }

  if (/^\d{14}$/.test(digits)) {
    return {
      type: 'CNPJ',
      masked: `**.${digits.slice(2, 5)}.${digits.slice(5, 8)}/****-**`,
      isValid: isValidCnpj(digits),
      raw,
      normalized: digits,
      message: isValidCnpj(digits) ? undefined : 'CNPJ invalido.',
    };
  }

  if (
    /^[a-f0-9]{32}$/i.test(raw) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(raw)
  ) {
    return {
      type: 'Aleatoria',
      masked: `${raw.slice(0, 8)}****${raw.slice(-8)}`,
      isValid: true,
      raw,
      normalized: raw,
    };
  }

  return {
    type: 'Invalida',
    masked: raw,
    isValid: false,
    raw,
    normalized: raw,
    message: 'Formato de chave PIX invalido.',
  };
};

export const normalizePixKeyOnBlur = (value: string) => {
  const info = getPixKeyInfo(value);
  return {
    value: info.normalized || (value || '').trim(),
    info,
  };
};
